import { format } from "date-fns"

// Helper function to format date for coupon display
function formatCouponDate(date: string | Date | null | undefined): string {
	if (!date) return ""
	try {
		const dateObj = date instanceof Date ? date : new Date(date)
		return format(dateObj, "MM-dd-yyyy HH:mm")
	} catch (error) {
		console.error("Error formatting coupon date:", error)
		return String(date) || ""
	}
}

export interface CouponTimeData {
	startTime?: string | Date | null
	endTime?: string | Date | null
	expiresAt?: string | Date | null
}

/**
 * Get coupon time display text based on the product team's requirements
 *
 * 用户优惠券的几种情况:
 * 1. 优惠券有开始时间、结束时间、过期时间，就展示开始时间-过期时间
 * 2. 优惠券有开始时间，没有结束时间、过期时间，就展示开始时间
 * 3. 优惠券没有开始时间，有结束时间或者过期时间，就优先展示过期时间，没有过期时间，展示结束时间
 * 4. 优惠券没有开始时间、结束时间，有过期时间，就展示过期时间
 * 5. 优惠券有开始时间、结束时间，没有过期时间，就展示开始时间-结束时间
 * 6. 优惠券没有开始时间、结束时间、过期时间，就不展示时间
 */
export function getCouponTimeDisplayText(
	timeData: CouponTimeData,
	t: any,
): string | null {
	const { startTime, endTime, expiresAt } = timeData

	// Case 1: 有开始时间、结束时间、过期时间，展示开始时间-过期时间
	if (startTime && endTime && expiresAt) {
		return `${formatCouponDate(startTime)} - ${formatCouponDate(expiresAt)}`
	}

	// Case 2: 有开始时间，没有结束时间、过期时间，展示开始时间
	if (startTime && !endTime && !expiresAt) {
		return t("coupon.startDate", "Start date: {{date}}", {
			date: formatCouponDate(startTime),
		})
	}

	// Case 7: 有开始时间、过期时间，没有结束时间，展示开始时间-过期时间
	if (startTime && expiresAt && !endTime) {
		return `${formatCouponDate(startTime)} - ${formatCouponDate(expiresAt)}`
	}

	// Case 3: 没有开始时间，有结束时间或者过期时间，优先展示过期时间，没有过期时间，展示结束时间
	if (!startTime && (endTime || expiresAt)) {
		if (expiresAt) {
			return t("coupon.expiresAt", "Expiry Date: {{date}}", {
				date: formatCouponDate(expiresAt),
			})
		} else if (endTime) {
			return t("coupon.endDate", "End date: {{date}}", {
				date: formatCouponDate(endTime),
			})
		}
	}

	// Case 4: 没有开始时间、结束时间，有过期时间，展示过期时间
	if (!startTime && !endTime && expiresAt) {
		return t("coupon.expiresAt", "Expiry Date: {{date}}", {
			date: formatCouponDate(expiresAt),
		})
	}

	// Case 5: 有开始时间、结束时间，没有过期时间，展示开始时间-结束时间
	if (startTime && endTime && !expiresAt) {
		return `${formatCouponDate(startTime)} - ${formatCouponDate(endTime)}`
	}

	// Case 6: 没有开始时间、结束时间、过期时间，不展示时间
	if (!startTime && !endTime && !expiresAt) {
		return null
	}

	// Fallback: 如果只有开始时间，展示开始时间
	if (startTime) {
		return t("coupon.startDate", "Start date: {{date}}", {
			date: formatCouponDate(startTime),
		})
	}

	// Fallback: 如果只有过期时间，展示过期时间
	if (expiresAt) {
		return t("coupon.expiresAt", "Expiry Date: {{date}}", {
			date: formatCouponDate(expiresAt),
		})
	}

	// Fallback: 如果只有结束时间，展示结束时间
	if (endTime) {
		return t("coupon.endDate", "End date: {{date}}", {
			date: formatCouponDate(endTime),
		})
	}

	return null
}
